'use client';

import { getFbCookies } from '../meta/getFbCookies';
import { firePixelEvent } from '../meta/firePixelEvent';
import { fireAdsConversion } from '../googleAds/fireAdsConversion';
import { requestConversion } from './requestConversion';
import type { GoogleAdsConversion, MetaConversion } from './types';

interface TrackConversionArgs {
  email?: string;
  sourceUrl?: string;
  formId?: string;
  formValues?: Record<string, string>;
  meta?: MetaConversion;
  googleAds?: GoogleAdsConversion;
}

/**
 * The server goes first: the platform holds the form's skip conditions, so
 * only its answer says whether this conversion counts. A refusal keeps the
 * browser quiet; anything else - success or a failed request - fires the
 * pixel and the Google Ads tag, all sharing one event id for deduplication.
 */
export async function trackConversion({
  email,
  sourceUrl,
  formId,
  formValues,
  meta,
  googleAds,
}: TrackConversionArgs): Promise<void> {
  const eventId = crypto.randomUUID();
  const currentUrl =
    sourceUrl || (typeof window !== 'undefined' ? window.location.href : '');

  const { track } = await requestConversion({
    eventId,
    sourceUrl: currentUrl,
    email,
    formId,
    formValues,
    meta: meta && { ...meta, ...fbCookies() },
  });

  if (!track) return;

  if (meta) {
    firePixelEvent({ ...meta, eventId });
  }

  if (googleAds) {
    await fireAdsConversion({ ...googleAds, transactionId: eventId, email });
  }
}

function fbCookies(): { fbp?: string; fbc?: string } {
  const { _fbp, _fbc } = getFbCookies();
  return { fbp: _fbp, fbc: _fbc };
}
